<template>
    <BaseBoxSection :helpName="helpPrefix + '-001'" :title="sectionTitle">

        <div :class="{'has-error': hasError('source') }" class="col-md-2">
            <label>{{ __('contract_source') }}*</label>
            <select2
                :disabled="disabled || (isIssuedDocument && ['invoicing_pl_margin','invoicing_pl_VAT'].includes(contractType.type_name))"
                :options="sourceOptions"
                :value="source"
                :settings="{ width: '100%' }"
                @input="emitInput('source', $event)"
            />

            <InputErrors :errors="getErrors('source')"/>
            <p :class="{'has-error': hasError('detailed') }" class="m-t-xs mb-0">
                <label>
                    <input
                        :checked="inputDetailed"
                        :disabled="isDisabledDetailedCb"
                        type="checkbox"
                        @change="emitInput('detailed', $event.target.checked)"
                    >
                    {{ __('contract-detailed') }}
                </label>
            </p>
        </div>

        <div :class="{'has-error': hasError('name') }" class="col-md-2">
            <InputWrapper
                :disabled="disabled"
                :errors="getErrors('name')"
                :label="__('contract-order-name') + '*'"
                :value="inputName"
                classNames="form-control"
                type="text"
                @input="emitInput('name', $event)"
            />
            <InputErrors :errors="getErrors('detailed')"/>
        </div>

        <div :class="{'has-error': hasError('number') }" class="col-md-2">
            <InputWrapper
                :disabled="disabled || (isIssuedDocument && ['invoicing_pl_margin','invoicing_pl_VAT'].includes(contractType.type_name))"
                :errors="getErrors('number')"
                :label="__('contract_number')"
                :value="inputNumber"
                classNames="form-control"
                type="text"
                @input="emitInput('number', $event)"
            />

        </div>

        <div :class="{'has-error': hasError('date') }" class="col-md-2">
            <label>{{ __('contract-order-date') + '*' }}</label>
            <datepicker
                :disabled="disabled || (isIssuedDocument && ['invoicing_pl_margin','invoicing_pl_VAT'].includes(contractType.type_name))"
                :initWithoutEmitEvent="true"
                :value="inputDate"
                @input="emitInput('date', $event)"
            />

            <InputErrors :errors="getErrors('date')"/>
        </div>

        <div class="col-md-2">
            <label>{{ __('type_of_contract_order') }}</label>
            <select2
                :disabled="isDisabledTypeOfContractSel"
                :options="typesOfContract"
                :value="inputTypeOfContract"
                :settings="{ width: '100%' }"
                @input="emitInput('type_of_contract', $event)"
            />
        </div>

        <div :class="{'has-error': hasError('terms_of_delivery') }" class="col-md-2">
            <InputWrapper
                :disabled="disabled || (isIssuedDocument && ['invoicing_pl_margin','invoicing_pl_VAT'].includes(contractType.type_name))"
                :errors="getErrors('terms_of_delivery')"
                :label="__('terms_of_delivery')"
                :value="terms_of_delivery"
                classNames="form-control"
                type="text"
                @input="emitInput('terms_of_delivery', $event)"
            />

        </div>


        <div v-if="contractType.isMargin" :class="{'has-error': hasError('margin_type_id') }" class="offset-10 col-md-2 ">
            <label>{{ __('margin_type') }}*</label>
            <select2
                :disabled="isIssuedDocument || mode === 'show'"
                :options="marginTypeOptions"
                :value="inputMarginType"
                :settings="{ width: '100%' }"
                @input="emitInput('margin_type_id', parseInt($event))"
            />

            <InputErrors :errors="getErrors('margin_type_id')"/>
        </div>
    </BaseBoxSection>
</template>

<script>
import BaseBoxSection from "../BaseBoxSection";
import InputWrapper from "../form/InputWrapper";
import hasErrors from "../../mixins/hasErrors";
import InputErrors from "../form/InputErrors";
import inputEmit from "../../mixins/inputEmit";

export default {
    name: "BasicInformationBox",
    components: {InputErrors, InputWrapper, BaseBoxSection},
    props: {
        mode: {
            type: String,
            required: true,
        },
        typesOfContract: {
            type: Array,
            default: [],
        },
        marginTypes: {
            type: Array,
            default: [],
        },
        contractTypeName: {
            type: String,
            default: '',
        },
        inputName: {
            type: String,
            default: '',
        },
        inputNumber: {
            type: String,
            default: '',
        },
        terms_of_delivery:{
            type: String,
            default: '',
        },
        inputTypeOfContract: {
            type: String,
            required: true,
        },
        inputDetailed: {
            type: Boolean,
            default: false,
        },
        inputDate: {
            type: String,
            default: '',
        },
        source:{
            type: String,
            default: '0',
        },
        inputMarginType: {
            type: Number,
            require: true,
        },
        contractType: {
            type: Object,
            required: true,
        },
        isConnectedToDocument: {
            type: Boolean,
            default: false,
        },
        isIssuedDocument: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        helpPrefix: {
            type: String,
            default: '',
        },
    },
    mixins: [hasErrors, inputEmit],
    computed: {
        sectionTitleTypeName() {
            switch (this.contractType.type_name) {
                case 'invoicing_pl_VAT': {
                    return 'contract-order-pl';
                }
                case 'invoicing_pl_margin': {
                    return 'contract-order-pl-margin';
                }
            }

            return ''
        },
        sectionTitle() {
            switch (this.mode) {
                case 'copy':
                case 'create': {
                    return this.__('add') + ' ' + this.__('contract_or_order').toLowerCase() + ' ' + this.__(this.sectionTitleTypeName);
                }
                case 'edit': {
                    return this.__('edition') + ' ' + this.__('contract_or_order_for_action') + ' ' + this.__(this.sectionTitleTypeName);
                }
                case 'show': {
                    return this.__('preview') + ' ' + this.__('contract_or_order_for_action') + ' ' + this.__(this.sectionTitleTypeName);
                }
            }
        },
        marginTypeOptions() {
            let options = this.marginTypes.map((type) => ({text: type.trans_name, id: type.id}));
            options.unshift({text: '---', id: 0});

            return options;
        },
        sourceOptions(){
            let options = [
                {text:this.__('agreement'), id:'contract'},
                {text:this.__('order'), id:'order'},
            ]
            options.unshift({text: '---', id: '0'});
            return options;
        },
        isDisabledDetailedCb() {
            return this.disabled || (this.isConnectedToDocument && !this.inputDetailed) || ['adding', 'adding_sell', 'adding_buy'].includes(this.contractType.type_name);
        },
        isDisabledTypeOfContractSel() {
            return this.mode === 'show' || !['adding', 'adding_sell', 'adding_buy'].includes(this.contractType.type_name) || this.isConnectedToDocument;
        },
    },
};
</script>
